import { RetrySafety } from '@/interfaces/print-bridge-protocol';

/**
 * Shared structural shape exposed by every print error that carries retry
 * semantics: the bridge's PrintBridgeError ('safe' | 'unsafe' | 'unknown')
 * and the native strategy's NativePrintError ('safe' | 'unknown').
 */
export interface IRetrySafetyCarrier {
  retrySafety: RetrySafety;
}

/**
 * Failure phase of a NativePrintError. 'status' is EXCLUSIVELY the post-send
 * DLE-EOT status check (bytes were fully written, the printer then reported
 * paper-out/offline/error) — job records map it to the 'unknown' status
 * ("Verificar"). Write-phase failures are 'send' even though their
 * retrySafety is also 'unknown' (retrySafety must never be the
 * discriminator: a torn write is NOT a status report).
 */
export type TNativePrintPhase = 'presend' | 'send' | 'status';

/**
 * Error thrown by the NATIVE printing strategy after its bounded retry loop
 * is exhausted. `retrySafety` is derived from the FAILURE PHASE:
 * - pre-send steps (handle creation, connect, permissions) -> 'safe'
 * - the send itself -> 'unknown' (bytes may have left), unless the plugin's
 *   numeric error code proves no bytes left.
 */
export class NativePrintError extends Error implements IRetrySafetyCarrier {
  readonly retrySafety: RetrySafety;
  readonly cause: unknown;
  readonly phase?: TNativePrintPhase;

  constructor(
    message: string,
    retrySafety: RetrySafety,
    cause?: unknown,
    phase?: TNativePrintPhase
  ) {
    super(message);
    this.name = 'NativePrintError';
    this.retrySafety = retrySafety;
    this.cause = cause;
    this.phase = phase;
  }
}

/**
 * True when the error itself declares a NOT-'safe' retry safety ('unsafe' or
 * 'unknown'): bytes may have reached the printer, so auto-print paths must
 * mark items as printed instead of leaving them for catch-up (an auto-reprint
 * could double-print).
 *
 * Deliberately checks the error's OWN field only — it must NOT unwrap
 * `cause`: a StickerBatchError / ComandaBatchError whose cause is unsafe must
 * still read as NOT-unsafe here, because the batch already marked
 * printed/ambiguous labels (or comanda units) before rethrowing and the
 * unmarked remainder relies on the frozen catch-up sync point. Sites that
 * need the wrapped cause inspect it explicitly.
 */
export function hasUnsafeRetrySafety(e: unknown): boolean {
  const safety = retrySafetyOf(e);
  return safety === 'unsafe' || safety === 'unknown';
}

/**
 * Job-record status for a print failure: 'unknown' ("Verificar")
 * EXCLUSIVELY for the native post-send DLE-EOT status phase — the bytes
 * were fully written and the printer then reported a problem, so the
 * document MAY have printed. retrySafety must NOT be the discriminator
 * (write-phase failures are also 'unknown'-safety but never 'status').
 */
export function jobStatusOfError(e: unknown): 'failed' | 'unknown' {
  return e instanceof NativePrintError && e.phase === 'status'
    ? 'unknown'
    : 'failed';
}

/**
 * The error's OWN declared retry safety, or undefined for plain Errors.
 * Same non-unwrapping rule as hasUnsafeRetrySafety (see above).
 */
export function retrySafetyOf(e: unknown): RetrySafety | undefined {
  if (!e || typeof e !== 'object') return undefined;
  const safety = (e as Partial<IRetrySafetyCarrier>).retrySafety;
  return safety === 'safe' || safety === 'unsafe' || safety === 'unknown'
    ? safety
    : undefined;
}
